import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, Container, TextField, Button, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { RefreshCw, UserPen, CalendarClock, Mail, Settings } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import OTPInput from '../components/OTPInput';
import UserHeader from '../components/UserHeader';
import MaintenanceBanner from '../components/MaintenanceBanner';
import { useMemberAuth } from '../context/MemberAuthContext';
import { useSettings } from '../context/SettingsContext';
import { useChatBot } from '../context/ChatBotContext';
import { apiFetch } from '../lib/supabase';

const PageContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa'
});

const HeaderSection = styled(Box)({
  backgroundImage: 'url("/external/swimming-pool-hero-1500w.png")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  padding: '60px 20px',
  textAlign: 'center'
});

const ContentContainer = styled(Container)({
  padding: '40px 20px',
  maxWidth: '800px'
});

const OptionCard = styled(Paper)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  padding: '24px',
  marginBottom: '16px',
  border: '2px solid #121212',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)'
  }
});

const IconWrapper = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: '#dbf14e',
  border: '2px solid #121212',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
});


const LoginCard = styled(Paper)({
  padding: '32px',
  border: '2px solid #121212',
  borderRadius: '16px',
  backgroundColor: '#fff'
});

const Footer = styled(Box)({
  backgroundColor: '#121212',
  color: '#fff',
  padding: '40px 20px',
  textAlign: 'center'
});

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Members = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, member, requestCode, verifyCode, logout, isDevEnvironment, setAuthFromToken } = useMemberAuth();
  const { settings, isRegistrationOpen, isSeasonClosed, getSeasonClosedMessage } = useSettings();
  const { setPageContent, clearPageContent } = useChatBot();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [memberData, setMemberData] = useState(null);
  const [isCurrentYearMember, setIsCurrentYearMember] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const pendingRedirectRef = useRef(null);
  
  const currentYear = new Date().getFullYear();
  // Out of season the login form is hidden and the server rejects member codes.
  // Admins are exempt server-side and sign in via /admin.
  const seasonClosed = isSeasonClosed();
  const seasonClosedMessage = getSeasonClosedMessage();

  useEffect(() => {
    const content = `CCSC Members Portal - Member Login and Services

Login Process:
- Enter your registered email address
- Receive a 6-digit verification code via email
- Enter the code to access your member portal

Available Options for Current Year Members:
- Update Details: Modify your contact information, address, phone number
- Check/Swap Duties: View your assigned duty shifts and request swaps with other members

Available Options for Previous Members:
- Renew Membership: Start the renewal process for the current season
- Update Details: Update your information before renewing
- Check/Swap Duties: View duty assignments

Member Features:
- View your membership status
- See which duties you're assigned to
- Request duty swaps with other members (when enabled)
- Update personal and family information

Security:
- One-time verification codes sent to your registered email
- Codes expire after 10 minutes
- Session automatically expires after 24 hours`;
    
    setPageContent(content);
    return () => clearPageContent();
  }, [setPageContent, clearPageContent]);

  useEffect(() => {
    const loginToken = searchParams.get('loginToken');
    const redirect = searchParams.get('redirect');
    const section = searchParams.get('section');
    const emailParam = searchParams.get('email');

    if (redirect) {
      pendingRedirectRef.current = { path: redirect, section: section || null };
    }

    if (emailParam && !isAuthenticated) {
      setEmail(emailParam);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('email');
      setSearchParams(newParams, { replace: true });
    }

    if (loginToken) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('loginToken');
      setSearchParams(newParams, { replace: true });

      if (!tokenLoading) {
        setTokenLoading(true);
        if (isAuthenticated) {
          logout();
        }

        (async () => {
          try {
            const response = await fetch('/api/member/verify-login-token', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: loginToken })
            });
            const data = await response.json();
            if (data.success && data.token && data.member) {
              setAuthFromToken(data.token, data.member);
            } else {
              setError(data.error || 'Login link has expired. Please log in with your email.');
            }
          } catch (err) {
            console.error('Token login error:', err);
            setError('Failed to verify login link. Please log in with your email.');
          } finally {
            setTokenLoading(false);
          }
        })();
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && member) {
      fetchMemberDetails(member.email);
    }
  }, [isAuthenticated, member]);

  const fetchMemberDetails = async (memberEmail) => {
    const DEV_ADMIN_EMAILS = ['leo.poll@poolle.co.uk'];
    
    try {
      let adminFound = false;
      try {
        const r = await apiFetch(`/api/admin/is-admin?email=${encodeURIComponent(memberEmail.trim())}`);
        adminFound = !!r.isAdmin;
      } catch (e) {
        adminFound = DEV_ADMIN_EMAILS.some(email =>
          email.toLowerCase() === memberEmail.trim().toLowerCase()
        );
      }

      if (!adminFound) {
        adminFound = DEV_ADMIN_EMAILS.some(email =>
          email.toLowerCase() === memberEmail.trim().toLowerCase()
        );
      }

      setIsAdmin(adminFound);

      const { member: foundMember } = await apiFetch(
        `/api/member/by-email?email=${encodeURIComponent(memberEmail.trim())}`
      );

      if (foundMember) {
        setMemberData(foundMember);

        const { years: membershipYears } = await apiFetch(`/api/member/${foundMember.id}/years`);
        const isCurrent = membershipYears.includes(currentYear);
        setIsCurrentYearMember(isCurrent);

        if (pendingRedirectRef.current) {
          const { path: redirect, section } = pendingRedirectRef.current;
          pendingRedirectRef.current = null;
          if (redirect === '/update-details') {
            navigate('/register', { state: { isUpdateOnly: true, memberData: foundMember, scrollToSection: section } });
            return;
          } else if (redirect === '/renew') {
            navigate('/register', { state: { isRenewal: true, memberData: foundMember } });
            return;
          } else if (redirect === '/duty-manager') {
            navigate('/duty-manager');
            return;
          }
        }
      }
    } catch (err) {
      console.error('Error fetching member details:', err);
    }
  };

  const handleRequestCode = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    const result = await requestCode(email);
    if (!result.success) {
      setError(result.message);
      setLoading(false);
      return;
    }
    setMessage(result.message);
    setStep('code');
    
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    const result = await verifyCode(email, code);
    
    if (result.success) {
      if (result.redirectTo) {
        // Admin user - redirect to admin page
        navigate(result.redirectTo);
        return;
      }
      if (result.member) {
        fetchMemberDetails(result.member.email);
      }
    } else {
      setError(result.error || 'Invalid verification code');
    }
    
    setLoading(false);
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const getOptions = () => {
    const registrationOpen = isRegistrationOpen();
    const renewOption = {
      id: 'renew',
      icon: RefreshCw,
      title: 'Renew my membership',
      description: registrationOpen ? 'Continue your membership for the new season' : 'Registration is not yet open',
      path: '/register',
      disabled: !registrationOpen
    };

    const updateOption = {
      id: 'update',
      icon: UserPen,
      title: 'Update my details',
      description: 'Change your contact information, duty availability etc.',
      path: '/register'
    };

    const dutiesOption = {
      id: 'duties',
      icon: CalendarClock,
      title: 'Duty Rota',
      description: 'View your assigned duties and see who\'s on duty each day',
      path: '/duty-manager'
    };

    const adminOption = {
      id: 'admin',
      icon: Settings,
      title: 'CCSC Administration',
      description: 'Manage members, settings, and club operations',
      path: '/admin'
    };

    if (!memberData && isAdmin) {
      return [adminOption];
    }

    const options = isCurrentYearMember 
      ? [updateOption, dutiesOption]
      : [renewOption, dutiesOption];
    
    if (isAdmin) {
      options.push(adminOption);
    }
    
    return options;
  };

  const handleOptionClick = (option) => {
    if (option.disabled) return;
    if (option.id === 'renew') {
      navigate(option.path, { state: { isRenewal: true, memberData: memberData } });
    } else if (option.id === 'update') {
      navigate(option.path, { state: { isUpdateOnly: true, memberData: memberData } });
    } else {
      navigate(option.path);
    }
  };

  const getHeaderSubtitle = () => {
    if (isAuthenticated && memberData) {
      return `Welcome back, ${memberData.first_name || 'Member'}!`;
    }
    if (isAuthenticated && isAdmin) {
      return 'Welcome, Administrator';
    }
    if (seasonClosed) {
      return 'Member login is closed for the season';
    }
    if (step === 'code') {
      return 'Enter the code sent to your email';
    }
    return 'Sign in with your email to continue';
  };

  return (
    <PageContainer>
      <UserHeader />
      <MaintenanceBanner message="The site is undergoing maintenance. Member login is temporarily unavailable — please check back shortly." />
      <HeaderSection>
        <Typography 
          variant="h3" 
          sx={{ 
            fontFamily: "'Chillax', sans-serif",
            fontWeight: 600,
            color: '#0d2c54',
            marginBottom: '8px'
          }}
        >
          Members Area
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            fontFamily: "'General Sans', sans-serif",
            color: '#4a5568'
          }}
        >
          {getHeaderSubtitle()}
        </Typography>
      </HeaderSection>

      <ContentContainer>
        <Breadcrumbs />

        {tokenLoading && (
          <LoginCard elevation={0} sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography sx={{ fontFamily: "'General Sans', sans-serif", color: '#666' }}>
              Signing you in...
            </Typography>
          </LoginCard>
        )}

        {!tokenLoading && !isAuthenticated && seasonClosed && (
          <LoginCard elevation={0}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Chillax', sans-serif",
                fontWeight: 600,
                color: '#0d2c54',
                marginBottom: '16px'
              }}
            >
              The pool is closed for the season
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'General Sans', sans-serif",
                color: '#666',
                marginBottom: '8px'
              }}
            >
              {seasonClosedMessage}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'General Sans', sans-serif",
                color: '#666'
              }}
            >
              Member login will reopen with the new season. If you need help in the
              meantime, email contact@comberton-pool.org.uk. Club administrators can
              sign in on the{' '}
              <Link to="/admin" style={{ color: '#0d2c54', fontWeight: 600 }}>
                admin page
              </Link>
              .
            </Typography>
          </LoginCard>
        )}

        {!tokenLoading && !isAuthenticated && !seasonClosed && step === 'email' && (
          <LoginCard elevation={0}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "'Chillax', sans-serif",
                fontWeight: 600,
                color: '#0d2c54',
                marginBottom: '16px'
              }}
            >
              Sign in to your account
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: "'General Sans', sans-serif",
                color: '#666',
                marginBottom: '20px'
              }}
            >
              Enter your email address to identify your account.
            </Typography>

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <TextField
                fullWidth
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleRequestCode)}
                disabled={loading}
                InputProps={{
                  startAdornment: <Mail size={18} color="#666" style={{ marginRight: '12px' }} />
                }}
                sx={{ 
                  flex: 1,
                  minWidth: '200px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontFamily: "'General Sans', sans-serif"
                  }
                }}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ marginBottom: '16px' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleRequestCode}
              disabled={loading || !isValidEmail(email)}
              fullWidth
              sx={{
                backgroundColor: '#dbf14e',
                color: '#0d2c54',
                border: '2px solid #121212',
                borderRadius: '100px',
                padding: '12px 24px',
                fontFamily: "'General Sans', sans-serif",
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#c9df3e'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Code'}
            </Button>
          </LoginCard>
        )}

        {!isAuthenticated && !seasonClosed && step === 'code' && (
          <LoginCard elevation={0}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "'Chillax', sans-serif",
                fontWeight: 600,
                color: '#0d2c54',
                marginBottom: '16px'
              }}
            >
              Enter verification code
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: "'General Sans', sans-serif",
                color: '#666',
                marginBottom: '24px'
              }}
            >
              We've sent a 6-digit code to <strong>{email}</strong>. Not received the code? Check your spam folder before contacting contact@comberton-pool.org.uk
            </Typography>

            {error && (
              <Alert severity="error" sx={{ marginBottom: '16px' }}>
                {error}
              </Alert>
            )}

            {message && (
              <Alert severity="success" sx={{ marginBottom: '16px' }}>
                {message}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
              <OTPInput
                value={code}
                onChange={setCode}
                disabled={loading}
              />
              <Button
                variant="contained"
                onClick={handleVerifyCode}
                disabled={loading}
                sx={{
                  backgroundColor: '#dbf14e',
                  color: '#0d2c54',
                  border: '2px solid #121212',
                  borderRadius: '100px',
                  padding: '12px 24px',
                  fontFamily: "'General Sans', sans-serif",
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#c9df3e'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <Button
                variant="text"
                onClick={async () => {
                  setLoading(true);
                  await requestCode(email);
                  setMessage(isDevEnvironment ? 'Dev mode: Enter code 123456 to log in' : 'Code resent! Check your inbox');
                  setLoading(false);
                }}
                disabled={loading}
                sx={{
                  color: '#0d2c54',
                  fontFamily: "'General Sans', sans-serif",
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Resend Code
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                  setMessage('');
                }}
                sx={{
                  color: '#666',
                  fontFamily: "'General Sans', sans-serif",
                  textTransform: 'none'
                }}
              >
                Use a different email
              </Button>
            </Box>
          </LoginCard>
        )}

        {isAuthenticated && (
          <>
            {!isCurrentYearMember && memberData && (
              <Alert 
                severity="info" 
                sx={{ 
                  marginBottom: '24px',
                  borderRadius: '12px',
                  fontFamily: "'General Sans', sans-serif"
                }}
              >
                Your membership for {currentYear} has not been renewed yet.
              </Alert>
            )}

            {getOptions().map((option) => (
              <OptionCard 
                key={option.id} 
                elevation={0}
                onClick={() => handleOptionClick(option)}
                sx={option.disabled ? {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  '&:hover': {
                    transform: 'none',
                    boxShadow: 'none'
                  }
                } : {}}
              >
                <IconWrapper sx={option.disabled ? { opacity: 0.5 } : {}}>
                  <option.icon size={24} color={option.disabled ? '#999' : '#0d2c54'} />
                </IconWrapper>
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: "'Chillax', sans-serif",
                      fontWeight: 600,
                      color: option.disabled ? '#999' : '#0d2c54',
                      marginBottom: '4px'
                    }}
                  >
                    {option.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: "'General Sans', sans-serif",
                      color: option.disabled ? '#999' : '#666'
                    }}
                  >
                    {option.description}
                  </Typography>
                </Box>
              </OptionCard>
            ))}

            <Link to="/rules" style={{ textDecoration: 'none' }}>
              <OptionCard>
                <IconWrapper>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0d2c54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </IconWrapper>
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: "'Chillax', sans-serif",
                      fontWeight: 600,
                      color: '#0d2c54',
                      marginBottom: '4px'
                    }}
                  >
                    View Club Rules
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: "'General Sans', sans-serif",
                      color: '#666'
                    }}
                  >
                    Read the club rules and guidelines
                  </Typography>
                </Box>
              </OptionCard>
            </Link>
          </>
        )}
      </ContentContainer>

      <Footer>
        <Typography 
          sx={{ 
            fontFamily: "'General Sans', sans-serif",
            color: '#ccc'
          }}
        >
          Comberton Community Swimming Club
        </Typography>
      </Footer>
    </PageContainer>
  );
};

export default Members;
